import React, { useRef, useCallback, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Typo, Flex, Icon, theme, SimpleButton } from '../../../../ui';

import { TASK_TYPE } from '../../../../utils/constants';
import { List } from '@mui/material';
import { useTasksActions } from '../../../tasks/hooks/useTasksActions';
import { useUpdateTaskResult, usePage } from '../../../viewCourse/hooks';
import Results from './Results';
import TextType from './TextType';
import SingleChoiceType from './SingleChoiceType';
import MultipleChoiceType from './MultipleChoiceType';
import { getLighterThemeColor, palette } from '../../../../ui/theme/colors';
import TaskHeaderMedia from './TaskHeaderMedia';
import { useCourseProgress } from '../../../course/hooks';
import { useNewViewCourseId } from '../../../../hooks/readFromUrls';
import { isModuleEmpty } from '../../../editCourse/utils/moduleUtils';
import { useFeatures } from '../../../settings/hooks';

const ResultsContainer = styled.div`
  padding: 20px;
  & ul {
    padding: 0;
    margin-top: 0;
  }
`;

const TaskContainer = styled.div`
  background-color: ${({ $bgColor }) => $bgColor};
  padding: 20px;
  border-radius: 11px;
`;

const Wrapper = styled.div`
  align-self: start;
  flex-basis: ${(props) => props.$split50 && '50%'};
  width: 100%;
`;

const ExplanationBox = styled.div`
  margin: 16px 0 0 0;
  background: ${palette.ora50};
  border: 1px solid ${palette.ora100};
  padding: 12px 16px;
  border-radius: 8px;
`;

const ExplanationHeader = styled(Flex)`
  margin-bottom: 6px;
`;

const FeedbackText = styled(Flex)`
  margin: 16px 0;
  background: ${({ $isCorrect }) => ($isCorrect ? palette.gre50 : palette.red50)};
  border: 1px solid ${({ $isCorrect }) => ($isCorrect ? palette.gre200 : palette.red200)};
  border-radius: 8px;
  padding: 4px 16px;
  gap: 6px;
`;

const ShowMoreContainer = styled(Flex)`
  justify-content: flex-end;
  padding-top: 8px;
`;

const TaskModule = ({
  data,
  module,
  isQuiz = false,
  themeColor,
  sessionResults = [],
  setSessionResults = null,
  setTaskTracking,
  currentTaskIndex,
  setCurrentTaskIndex,
  totalTasks,
  split50,
}) => {
  const { t } = useTranslation();
  const courseId = useNewViewCourseId();
  const updateTaskResult = useUpdateTaskResult();
  const { refetch: refetchProgress } = useCourseProgress(courseId);
  const pageData = usePage(); // Get updated page data from Redux
  const features = useFeatures();
  const hideTaskResultOnAnswer = features?.showTaskResultOnAnswer;

  const { answerOnTask } = useTasksActions();

  const textTaskResultRef = useRef({});
  const taskContent = data.params;

  const { task } = taskContent;

  // Get updated taskResults from Redux state instead of static props
  const updatedModule = pageData?.pageRows?.flatMap((row) => row.pageModules)?.find((mod) => mod.id === module.id);
  const taskResults = updatedModule?.data?.params?.taskResults || taskContent.taskResults || [];

  const { main, items, comment } = task.caption;
  const { type } = task;

  const setLocalResult = useCallback(
    (value) => {
      // Guard against missing callbacks (e.g., in edit mode where quiz session isn't active)
      if (typeof setTaskTracking === 'function') {
        setTaskTracking((prevState) => ({ ...prevState, [task.taskId]: true }));
      }

      if (typeof setSessionResults === 'function') {
        const existingTaskIndex = sessionResults.findIndex((result) => result.taskId === task.taskId);

        if (existingTaskIndex !== -1) {
          const updatedResults = [...sessionResults];
          updatedResults[existingTaskIndex] = { taskId: task.taskId, value };
          setSessionResults(updatedResults);
        } else {
          setSessionResults([...sessionResults, { taskId: task.taskId, value }]);
        }
      }

      if (typeof setCurrentTaskIndex === 'function' && currentTaskIndex < totalTasks - 1) {
        setCurrentTaskIndex(currentTaskIndex + 1);
      }
    },
    [sessionResults, setSessionResults, task.taskId, setTaskTracking, totalTasks, currentTaskIndex, setCurrentTaskIndex],
  );

  const answerTask = useCallback(
    async (value) => {
      if (isQuiz) setLocalResult(value);

      try {
        const answerRes = await answerOnTask({
          pmid: module.id,
          taskId: task.taskId,
          value,
          isQuiz,
          courseId,
        });

        const taskResult = answerRes?.taskResult;
        if (!taskResult) return;

        updateTaskResult({
          ...taskResult,
          moduleId: module.id,
        });

        if (courseId) {
          await refetchProgress();
        }
      } catch (error) {
        console.error('Failed to answer task:', error);
      }
    },
    [answerOnTask, module.id, task.taskId, isQuiz, setLocalResult, courseId, refetchProgress, updateTaskResult],
  );

  const isEmpty = isModuleEmpty(module);

  // Track feedback state from current session submission
  const [feedbackState, setFeedbackState] = useState(null); // null | { isCorrect, correctCount?, totalCorrect? }

  // Track whether to show all results or just the last 3
  const [showAllResults, setShowAllResults] = useState(false);
  const RESULTS_LIMIT = 3;

  // Callback for choice components to notify answer result
  const onAnswerResult = useCallback((result) => {
    setFeedbackState(result);
  }, []);

  // Show explanation only after incorrect answer in current session (not in quiz mode)
  const shouldShowExplanation = !isQuiz && feedbackState && !feedbackState.isCorrect && Boolean(comment);

  // Filter results to show (last 3 or all)
  const visibleResults = showAllResults ? taskResults : taskResults.slice(-RESULTS_LIMIT);
  const hasMoreResults = taskResults.length > RESULTS_LIMIT;

  if (isEmpty) return null;

  const taskHeaderFile = task?.frontend?.length ? JSON.parse(task.frontend) : task.captionFiles && task.captionFiles[0];

  return (
    <Wrapper $split50={split50}>
      <TaskContainer $bgColor={getLighterThemeColor(themeColor)}>
        <Typo.ContentSmallHeader $textAlign='left' $marginBottom='10px'>
          {main}
        </Typo.ContentSmallHeader>
        {!!taskHeaderFile && <TaskHeaderMedia data={taskHeaderFile} themeColor={themeColor} />}
        <div>
          {shouldShowExplanation && (
            <ExplanationBox>
              <ExplanationHeader align='center' gap='8'>
                <Icon.Info size='small' color={theme.colors.SECONDARY} />
                <Typo.TextTitle color={theme.colors.NEU_PRIMARY}>{t('tasks.results.explanation')}</Typo.TextTitle>
              </ExplanationHeader>
              <Typo.MediumText color={theme.colors.NEU_600}>{comment}</Typo.MediumText>
            </ExplanationBox>
          )}
          {feedbackState && !isQuiz && (
            <FeedbackText align='center' $isCorrect={feedbackState.isCorrect}>
              {feedbackState.isCorrect ? (
                <>
                  <Icon.Check size='small' color={theme.colors.SUCCESS_GREEN} />
                  <Typo.SmallText color={theme.colors.SUCCESS_GREEN}>{t('tasks.results.correct')}</Typo.SmallText>
                </>
              ) : (
                <>
                  <Icon.Close size='small' color={theme.colors.ERROR_RED} />
                  <Typo.SmallText color={theme.colors.ERROR_RED}>
                    {feedbackState.totalCorrect
                      ? t('tasks.results.incorrectMultiple', {
                          correctCount: feedbackState.correctCount,
                          totalCorrect: feedbackState.totalCorrect,
                        })
                      : t('tasks.results.incorrect')}
                  </Typo.SmallText>
                </>
              )}
            </FeedbackText>
          )}
        </div>

        {(type === TASK_TYPE.OPEN || type === TASK_TYPE.FILE) && (
          <TextType data={task} taskRef={textTaskResultRef} onSubmit={answerTask} themeColor={themeColor} />
        )}
        {type === TASK_TYPE.SINGLE && (
          <SingleChoiceType results={sessionResults} data={task} t={t} onSubmit={answerTask} themeColor={themeColor} onAnswerResult={onAnswerResult} />
        )}
        {type === TASK_TYPE.MULTIPLE && (
          <MultipleChoiceType results={sessionResults} data={task} t={t} onSubmit={answerTask} themeColor={themeColor} onAnswerResult={onAnswerResult} />
        )}
      </TaskContainer>
      {!!taskResults.length && !isQuiz && (type === TASK_TYPE.OPEN || type === TASK_TYPE.FILE || !hideTaskResultOnAnswer) && (
        <ResultsContainer>
          <List sx={{ width: '100%' }}>
            {visibleResults.map(
              (result) => !!result?.id && <Results taskOptions={items} result={result} taskType={type} key={result?.id} themeColor={themeColor} />,
            )}
          </List>
          {hasMoreResults && (
            <ShowMoreContainer>
              <SimpleButton
                variant='ghost'
                size='small'
                endIcon={showAllResults ? 'ExpandLess' : 'ExpandMore'}
                onClick={() => setShowAllResults(!showAllResults)}
              >
                {showAllResults ? t('globals.showLess') : `${t('globals.showMore')} (${taskResults.length - RESULTS_LIMIT} ${t('globals.more')})`}
              </SimpleButton>
            </ShowMoreContainer>
          )}
        </ResultsContainer>
      )}
    </Wrapper>
  );
};

export default TaskModule;
